import Row from '@components/Row';
import Text from '@components/Text';
import colors from '@constants/colors';
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import CloseLineIcon from '@icons/CloseLineIcon';
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export interface Props {
    isOpen: boolean;
    close: () => void;
    title: string;
    options?: {value: string, label: string}[];
    onChange?: (v: string) => void;
}

function BottomSheetComponent({
    isOpen, close, title, options, onChange,
}:Props) {
    const bgRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (isOpen) {
            bgRef.current?.classList.remove(none);
            const showTimeout = setTimeout(() => {
                bgRef.current?.classList.remove(hide);
            }, 50);
            return () => clearTimeout(showTimeout);
        }
        bgRef.current?.classList.add(hide);
        const closeTimeout = setTimeout(() => {
            bgRef.current?.classList.add(none);
        }, 200);
        return () => clearTimeout(closeTimeout);
    }, [isOpen]);
    return (
        <div ref={bgRef}
            onClick={close}
            className={`${background} ${hide} ${none}`}>
            <Container onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
            }} >
                <Row justify='space-between'
                    fullWidth >
                    <Text color={colors.gray6} >
                        {title}
                    </Text>
                    <CloseLineIcon onClick={close}
                        size={24}/>
                </Row>
                {options?.map((option, index) => (
                    <FilterText key={`option ${index}`}
                        onClick={() => {
                            close();
                            onChange?.(option.value);
                        }} >
                        {option.label}
                    </FilterText>
                ))}
            </Container>
        </div>
    );
}

function BottomSheet(props: Props) {
    return createPortal(<BottomSheetComponent {...props} />, document.body);
}

// classList로 직접 토글하므로 @emotion/css 클래스로 선언
// hide, none은 background보다 뒤에 선언되어야 같은 명시도에서 우선 적용된다.
const background = css`
    align-items: flex-end;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    height: 100vh;
    left: 0;
    position: fixed;
    top: 0;
    transition: opacity linear 200ms;
    width: 100vw;
    z-index: 100;
    justify-content: center;
`;

const hide = css`
    opacity: 0.4;

    & > * {
        transform: translateY(100%);
    }
`;

const none = css`
    display: none;
`;

const Container = styled.div`
    background-color: var(--color-white);
    border-radius: 12px 12px 0 0;
    display: flex;
    flex-direction: column;
    padding: 20px 18px;
    row-gap: 16px;
    transition: 200ms;
    width: 100%;
`;

const FilterText = styled(Text)`
    cursor: pointer;
    padding: 12px 4px;
`;

export default BottomSheet;
